import type { Job } from "./models";

/**
 * How an actual shift's job label behaves for the employee who worked it.
 *
 * - `editable` — holds several jobs, so the shift's job can be reassigned in place (DAT-502).
 * - `explainable` — holds exactly one job. There is nothing to switch to, so instead of sitting
 *   inert the label explains how to add a second job first (DAT-615).
 * - `inert` — holds no jobs. Nothing truthful to say about which job the shift should carry, and
 *   the actual table's Job column is not feature-flag gated, so departments with job management
 *   off would otherwise get the hint on every row.
 */
export type ActualJobAffordance = { kind: "editable" } | { kind: "explainable"; job: Job } | { kind: "inert" };

/**
 * Classifies the job affordance from the employee's own job list. Keyed off `jobs` (the hydrated
 * form) rather than `jobIds` on purpose: `EditJobPopover` lists `nurse.jobs`, so a `jobIds`-based
 * gate could promise an edit menu that renders empty. An absent or empty list is `inert`.
 *
 * The `explainable` case carries the job itself so callers name the employee's current job rather
 * than the shift's — the two diverge after an HRIS job change, and the hint is a claim about the
 * employee. It also means no caller needs a fallback for a missing name.
 *
 * Lives apart from `shiftJobUpdate.ts` so it stays importable on its own: that module reaches into
 * `HospitalDataProvider`, which initializes Mixpanel at import time.
 */
export function getActualJobAffordance(jobs: Job[] | undefined): ActualJobAffordance {
	if (!jobs || jobs.length === 0) return { kind: "inert" };
	if (jobs.length > 1) return { kind: "editable" };
	return { kind: "explainable", job: jobs[0] };
}
